import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useVoucherTypes, VOUCHER_ICONS } from "../lib/voucherTypes";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Plane,
  Download,
  FileText,
  MessageCircle,
  Globe,
  FileCheck,
  Info,
  Car,
  Hotel,
  MapPin,
  Shield,
  Compass,
  Route,
  Camera,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_COLORS = [
  "from-cyan-500 to-sky-600", "from-sky-500 to-cyan-500", "from-sky-600 to-blue-500", "from-sky-500 to-teal-500",
  "from-cyan-400 to-sky-500", "from-sky-700 to-blue-600", "from-sky-500 to-indigo-500", "from-teal-500 to-sky-500",
];

export default function ClientView() {
  const { token } = useParams();
  const [client, setClient] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [logo, setLogo] = useState(null);
  const [agencyName, setAgencyName] = useState("Agencia de Viajes");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const voucherTypes = useVoucherTypes();

  useEffect(() => {
    loadData();
  }, [token]);

  useEffect(() => {
    const prev = document.title;
    document.title = "Media Ciudad Viajes | Mis vouchers";
    return () => { document.title = prev; };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      try {
        const settingsRes = await axios.get(`${API}/settings`);
        setAgencyName(settingsRes.data.agency_name);
        const hasLogo =
          settingsRes.data.logo_gridfs_id || settingsRes.data.logo_filename;
        if (hasLogo) {
          setLogo(`${API}/settings/logo?t=${Date.now()}`);
        }
      } catch (err) {
        console.error("Error loading settings");
      }

      const clientRes = await axios.get(`${API}/public/client/${token}`);
      setClient(clientRes.data);

      const vouchersRes = await axios.get(`${API}/public/vouchers/${token}`);
      setVouchers(vouchersRes.data);

      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al cargar los datos");
      setLoading(false);
    }
  };

  const handleDownload = async (voucherId, filename, retryCount = 0) => {
    const maxRetries = 2;
    try {
      toast.loading("Preparando descarga...", { id: `download-${voucherId}` });
      const response = await axios.get(
        `${API}/public/download/${token}/${voucherId}`,
        { responseType: "blob", timeout: 30000 }
      );
      if (!response.data || response.data.size === 0) throw new Error("Archivo vacío");

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("Descarga completada", { id: `download-${voucherId}` });
    } catch (err) {
      if (retryCount < maxRetries) {
        toast.loading(`Reintentando descarga... (${retryCount + 1}/${maxRetries})`, {
          id: `download-${voucherId}`,
        });
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return handleDownload(voucherId, filename, retryCount + 1);
      }
      const errorMsg =
        err.response?.status === 404
          ? "Archivo no encontrado"
          : err.code === "ECONNABORTED"
          ? "Tiempo de espera agotado. Intenta nuevamente"
          : "Error al descargar el archivo";
      toast.error(errorMsg, { id: `download-${voucherId}` });
    }
  };

  const getVoucherInfo = (type) => {
    const idx = voucherTypes.types.findIndex((t) => t.key === type);
    const t = voucherTypes.types[idx] || voucherTypes.byKey.otros || { label: "Otros", icon: "file-text" };
    return {
      label: t.label,
      Icon: VOUCHER_ICONS[t.icon] || FileText,
      color: TYPE_COLORS[Math.max(idx, 0) % TYPE_COLORS.length],
      hasCheckin: !!t.has_checkin,
      hasCarConditions: !!t.has_car_conditions,
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sky-gradient">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mx-auto"></div>
          <p className="mt-4 text-sky-700 font-heading">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sky-gradient p-4">
        <Card className="max-w-md glass-card border-0" data-testid="error-card">
          <CardHeader>
            <CardTitle className="text-red-600 font-heading">Error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-gradient flex flex-col">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-xl border-b border-sky-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4 flex-wrap">
            {logo ? (
              <img
                src={logo}
                alt="Logo"
                className="h-16 w-16 object-contain rounded-xl"
                data-testid="agency-logo"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div className="btn-sky p-3 rounded-2xl shadow-lg">
                <Plane className="w-10 h-10 text-white" />
              </div>
            )}
            <div>
              <h1
                className="text-3xl font-bold font-heading text-sky-900"
                data-testid="agency-name-header"
              >
                {agencyName}
              </h1>
              <p className="text-sky-700/80 mt-1">Bienvenido, {client?.name}</p>
              {client?.trip_destination && (
                <p className="text-sm text-sky-600 mt-0.5" data-testid="client-trip-header">
                  Tu viaje a <strong>{client.trip_destination}</strong>
                  {client.trip_date &&
                    ` · ${new Date(client.trip_date).toLocaleDateString("es-AR", { timeZone: "UTC" })}`}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full">
        <div className="mb-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-heading text-sky-900 mb-3">
            Tus Documentos de Viaje
          </h2>
          <p className="text-sky-700/80 max-w-2xl mx-auto text-base">
            Aquí encontrarás todos tus vouchers y documentos importantes. Haz clic en cada
            uno para descargarlos.
          </p>
        </div>

        {vouchers.length === 0 ? (
          <Card className="glass-card border-0 shadow-xl" data-testid="no-vouchers-card">
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 mx-auto text-sky-400 mb-4" />
              <h3 className="text-xl font-semibold text-sky-900 mb-2 font-heading">
                No hay vouchers disponibles
              </h3>
              <p className="text-sky-700/70">
                Tus documentos aparecerán aquí cuando estén listos.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {vouchers.map((voucher) => {
              const info = getVoucherInfo(voucher.type);
              const Icon = info.Icon;
              return (
                <Card
                  key={voucher.id}
                  className="glass-card border-0 shadow-lg hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
                  data-testid={`voucher-card-${voucher.id}`}
                >
                  <div className={`h-1.5 bg-gradient-to-r ${info.color}`}></div>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`bg-gradient-to-br ${info.color} p-3 rounded-xl shadow-md`}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0">
                        <CardTitle className="text-xl font-heading text-sky-900">
                          {info.label}
                        </CardTitle>
                        <CardDescription
                          className="text-xs mt-1 truncate"
                          title={voucher.original_filename}
                        >
                          {voucher.original_filename}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button
                      onClick={() =>
                        handleDownload(voucher.id, voucher.original_filename)
                      }
                      className={`w-full bg-gradient-to-r ${info.color} hover:opacity-90 transition-all shadow-md text-white`}
                      data-testid={`download-button-${voucher.id}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Descargar PDF
                    </Button>

                    {info.hasCheckin && (
                      <Button
                        onClick={() =>
                          window.open(
                            "https://mediaciudadviajes.com/web-check-in/",
                            "_blank"
                          )
                        }
                        variant="outline"
                        className="w-full border-sky-300 text-sky-700 hover:bg-sky-50"
                        data-testid={`checkin-button-${voucher.id}`}
                      >
                        <FileCheck className="w-4 h-4 mr-2" />
                        Check-in Online
                      </Button>
                    )}

                    {info.hasCarConditions && (
                      <Button
                        onClick={() =>
                          window.open(
                            "https://mediaciudadviajes.com/politicas-alquiler-de-autos-en-el-exterior/",
                            "_blank"
                          )
                        }
                        variant="outline"
                        className="w-full border-sky-300 text-sky-700 hover:bg-sky-50"
                        data-testid={`car-conditions-button-${voucher.id}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Condiciones de alquiler
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Client Notes */}
        {client?.notes && (
          <Card
            className="mt-10 glass-card border-0 border-l-4 !border-l-sky-500"
            data-testid="client-notes-card"
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sky-900 font-heading">
                <Info className="w-5 h-5" />
                Notas Importantes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Alert className="bg-sky-50/50 border-sky-200">
                <AlertDescription className="text-sky-900 whitespace-pre-wrap">
                  {client.notes}
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white/80 backdrop-blur-md border-t border-sky-100 mt-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-sm text-sky-700">
                ¿Tienes alguna pregunta? Contactanos
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap justify-center">
              <a
                href="http://wa.me/542233444716"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors shadow-md"
                data-testid="whatsapp-link"
              >
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm font-medium">WhatsApp</span>
              </a>

              <a
                href="https://www.mediaciudadviajes.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 btn-sky text-white rounded-lg transition-colors shadow-md"
                data-testid="website-link"
              >
                <Globe className="w-5 h-5" />
                <span className="text-sm font-medium">Nuestra Web</span>
              </a>

              <a
                href="https://mediaciudadviajes.com/condiciones-generales/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-sky-900 hover:bg-sky-800 text-white rounded-lg transition-colors shadow-md"
                data-testid="conditions-link"
              >
                <FileText className="w-5 h-5" />
                <span className="text-sm font-medium">Condiciones</span>
              </a>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-sky-100 text-center">
            <p className="text-xs text-sky-600/70">
              © {new Date().getFullYear()} {agencyName}. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
